/**
 * A pushed list that doesn't move under the user. Not frozen, it shows the
 * latest rows. Frozen (a later page, a drawer open, a selection, the pointer
 * on the table), it keeps the row order it had: rows still there show their
 * latest data in place, new rows wait (`pendingNew`), and rows gone from the
 * server stay where they were, dimmed (`removedIds`, `pendingRemoved`), until
 * `apply()` or unfreezing. Show both counts in a `LiveUpdatesPill`.
 */

import { type PointerEvent, useCallback, useMemo, useState } from "react";

interface Snapshot<T> {
  /** Retaken when this changes: another page or filter is another list. */
  key: string;
  order: string[];
  /** The newest data seen for each row, so a removed one still renders. */
  last: Map<string, T>;
  /** The rows `last` was brought up to date with. */
  source: T[];
  /** Rows the user's own action took out: they go, not dim. */
  dropped: ReadonlySet<string>;
}

interface Options<T> {
  rows: T[] | undefined;
  /** Stable (module-level) so the result memoizes. */
  getId: (row: T) => string;
  frozen: boolean;
  /** Identifies the list (page, filters): a new value starts over. */
  resetKey: string;
}

export interface LiveList<T> {
  rows: T[] | undefined;
  /** Rows shown dimmed: gone from the server, kept in place while frozen. */
  removedIds: ReadonlySet<string>;
  pendingNew: number;
  pendingRemoved: number;
  /** Show the latest rows (and keep holding them while still frozen). */
  apply: () => void;
  /** Take a row out at once, for an optimistic removal by the user. */
  drop: (id: string) => void;
}

const NONE: ReadonlySet<string> = new Set();

function take<T>(rows: T[], getId: (row: T) => string, key: string): Snapshot<T> {
  return {
    key,
    order: rows.map(getId),
    last: new Map(rows.map((r) => [getId(r), r])),
    source: rows,
    dropped: NONE,
  };
}

export function useLiveList<T>({ rows, getId, frozen, resetKey }: Options<T>): LiveList<T> {
  const [snap, setSnap] = useState<Snapshot<T> | null>(null);

  // Brought up to date during render (React's "adjust state on a prop change").
  let current = snap;
  if (!frozen || !rows) current = null;
  else if (!current || current.key !== resetKey) current = take(rows, getId, resetKey);
  else if (current.source !== rows) {
    const last = new Map(current.last);
    for (const r of rows) last.set(getId(r), r);
    current = { ...current, last, source: rows };
  }
  if (current !== snap) setSnap(current);

  const view = useMemo(() => {
    if (!current || !rows) return { rows, removedIds: NONE, pendingNew: 0 };
    const latest = new Map(rows.map((r) => [getId(r), r]));
    const shown: T[] = [];
    const removedIds = new Set<string>();
    for (const id of current.order) {
      const row = latest.get(id);
      if (row) shown.push(row);
      else if (!current.dropped.has(id)) {
        shown.push(current.last.get(id)!);
        removedIds.add(id);
      }
    }
    const known = new Set(current.order);
    const pendingNew = rows.reduce((n, r) => (known.has(getId(r)) ? n : n + 1), 0);
    return { rows: shown, removedIds, pendingNew };
  }, [current, rows, getId]);

  const apply = useCallback(() => {
    setSnap((s) => (s && s.source ? take(s.source, getId, s.key) : s));
  }, [getId]);
  const drop = useCallback((id: string) => {
    setSnap((s) => (s ? { ...s, dropped: new Set(s.dropped).add(id) } : s));
  }, []);

  return { ...view, pendingRemoved: view.removedIds.size, apply, drop };
}

/**
 * Whether a mouse pointer is over an element: a list without pages, drawers
 * or selections holds still while someone is about to click a row.
 */
export function usePointerInside() {
  const [inside, setInside] = useState(false);
  const handlers = useMemo(
    () => ({
      onPointerEnter: (e: PointerEvent) => {
        if (e.pointerType === "mouse") setInside(true);
      },
      onPointerLeave: () => setInside(false),
    }),
    [],
  );
  return { inside, handlers };
}
